import React from 'react';
import { Card, Button, Alert, useToast } from '@parsina/framework';

import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useMe } from '../../hooks/useMe.js';
import { errorField, errorCode, errorMessageKey } from '../../lib/bffClient.js';
import OnboardingFields from '../Onboarding/OnboardingFields.jsx';
import {
  profileToForm, validateOnboarding, toProfileUpdateInput, fieldError,
} from '../Onboarding/Onboarding.shared.js';

// «مشخصات» on the profile — the onboarding form, later. core.md §7.8.
//
// The SAME fields, the SAME rules, the SAME component: a student who typed their
// university once should recognise the screen where they fix a typo in it, and
// two forms over one row is how one of them ends up validating a student number
// the other accepts.
//
// The v1 comment that used to live here said "nothing on this screen is
// editable, because the contract has no mutation for it". That was true of v1
// and is not true of v2: `updateStudentProfile` exists, every field on it is
// optional, an omitted field is untouched and `""` clears one. What is still not
// editable here is the PHONE — it belongs to Keycloak and is changed there,
// which is what `profile.identity.note` says.
//
// The student number can be changed, deliberately: it is not a credential, it is
// not unique, and it is not verified against anything (core.md §4.5). A student
// who mistyped it must be able to fix it without support.

export default function ProfileIdentityForm({ phone }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { me, updateProfile } = useMe();

  const [values, setValues] = React.useState(() => profileToForm(me));
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [problem, setProblem] = React.useState('');

  // Adopt the stored profile when it lands — but never over typing in progress.
  // The store republishes on every balance refresh, and an unguarded sync would
  // wipe a half-typed university every time an AI answer came back.
  const hydrated = React.useRef(false);
  React.useEffect(() => {
    if (hydrated.current || !me) return;
    hydrated.current = true;
    setValues(profileToForm(me));
  }, [me]);

  const onChange = React.useCallback((patch) => {
    setValues((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
    setProblem('');
  }, []);

  const onSave = async () => {
    const problems = validateOnboarding(values);
    setErrors(problems);
    if (Object.keys(problems).length) return;

    setSaving(true);
    setProblem('');
    try {
      // The whole form, not a diff: every field on `StudentProfileUpdateInput`
      // is optional and an omitted one is UNTOUCHED, so sending the seven the
      // student is looking at is exactly "save what is on screen". A diff would
      // be the right shape for a patient (whose form is a subset of the row)
      // and the wrong one here.
      //
      // …with the one exception the contract forces: an optional field the
      // student EMPTIED cannot be omitted (omitted = keep the old value), so
      // `toProfileUpdateInput` sends the explicit clearing for it (core.md
      // §4.3, «"" clears»). That is why it takes the stored profile as well.
      await updateProfile(toProfileUpdateInput(values, me));
      toast.success(t('profile.saved'));
    } catch (err) {
      const field = errorField(err);
      if (errorCode(err) === 'INVALID_INPUT' && field) {
        // The BFF's own sentence under the field when it gave one, the
        // catalogue's generic line when it did not.
        setErrors((prev) => ({ ...prev, [field]: fieldError(err) }));
      }
      setProblem(t(errorMessageKey(err)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-testid="profile-identity">
      <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
        {t('profile.sections.identity')}
      </h2>

      {problem && <Alert variant="error" data-testid="profile-error">{problem}</Alert>}

      <OnboardingFields
        values={values}
        onChange={onChange}
        errors={errors}
        disabled={saving}
        phone={phone}
      />

      <div>
        <Button onClick={onSave} disabled={saving} data-testid="profile-save">
          {saving ? t('common.actions.saving') : t('common.actions.save')}
        </Button>
      </div>
    </Card>
  );
}
